<script setup lang="ts">
  import { IconCheck, IconPortcall } from '@gat-ship/icons'
  import {
    Sidebar,
    SidebarContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import type { StepGroup } from './create-portcall-steps'

  /** One step's display state, computed by the wizard. */
  export interface SidebarStep {
    key: string
    group: StepGroup
    label: string
    /** The step holds a complete value (shows a check). */
    hasValue: boolean
    /** The step currently being edited. */
    current: boolean
    /** Locked until a prerequisite step is filled. */
    locked: boolean
    /** Optional step (the pilot-station arrival). */
    optional: boolean
    /** Display value under the label, or '' when none. */
    value: string
    /** Hint shown when locked. */
    lockText: string
  }

  /**
   * The created port call shown once creation succeeds. The wizard steps
   * collapse away and this becomes the rail's new root — the anchor for any
   * future post-create steps run inside the modal.
   */
  export interface CreatedSidebar {
    label: string
  }

  defineProps<{
    steps: SidebarStep[]
    created: CreatedSidebar | null
  }>()
  const emit = defineEmits<{ select: [key: string] }>()

  function isClickable(step: SidebarStep): boolean {
    return !step.locked && !step.current
  }
</script>

<template>
  <SidebarProvider class="bg-muted/40 h-full min-h-0 w-full">
    <Sidebar collapsible="none" class="w-full bg-transparent">
      <SidebarContent class="gap-0 overflow-auto p-5">
        <SidebarMenu class="gap-0.5">
          <TransitionGroup name="step-collapse">
            <!-- creation steps: collapse away top-to-bottom once created -->
            <SidebarMenuItem
              v-for="(step, index) in created ? [] : steps"
              :key="step.key"
              :style="{ '--leave-i': index }"
            >
              <SidebarMenuButton
                class="h-auto items-start gap-2.5 py-1.5"
                :is-active="step.current"
                :disabled="!isClickable(step)"
                @click="emit('select', step.key)"
              >
                <span
                  :class="[
                    'mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-full',
                    step.hasValue || step.current
                      ? 'bg-primary'
                      : step.locked
                        ? 'bg-muted border-border border-[1.5px]'
                        : 'bg-background border-input border-[1.5px]'
                  ]"
                >
                  <IconCheck
                    v-if="step.hasValue"
                    class="text-primary-foreground size-2.5"
                  />
                  <span
                    v-else-if="step.current"
                    class="bg-primary-foreground size-1.5 rounded-full"
                  />
                </span>
                <div class="min-w-0 flex-1">
                  <div
                    :class="[
                      'truncate text-[13px] leading-tight',
                      step.current
                        ? 'font-semibold'
                        : step.locked
                          ? 'text-muted-foreground/60 font-medium'
                          : step.hasValue
                            ? 'text-foreground font-medium'
                            : 'text-muted-foreground font-medium'
                    ]"
                  >
                    {{ step.label }}
                  </div>
                  <div
                    v-if="
                      step.optional &&
                      !step.hasValue &&
                      !step.locked &&
                      !step.value
                    "
                    class="text-muted-foreground/60 mt-0.5 truncate text-[10px] font-medium"
                  >
                    Optional
                  </div>
                  <div
                    v-if="step.value"
                    class="text-muted-foreground mt-0.5 truncate text-[11px] font-medium"
                  >
                    {{ step.value }}
                  </div>
                  <div
                    v-if="step.locked"
                    class="text-muted-foreground/60 mt-0.5 truncate text-[10.5px] font-medium"
                  >
                    {{ step.lockText }}
                  </div>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <!-- created port call: the rail's new root for post-create steps;
                 eases in after the last step has collapsed -->
            <SidebarMenuItem
              v-if="created"
              key="created"
              :style="{ '--leave-count': steps.length }"
            >
              <SidebarMenuButton
                class="h-auto items-center gap-2.5 py-2"
                :is-active="true"
                disabled
              >
                <span
                  class="bg-primary flex size-[22px] shrink-0 items-center justify-center rounded-md"
                >
                  <IconPortcall class="text-primary-foreground size-3.5" />
                </span>
                <div class="min-w-0 flex-1">
                  <div
                    class="text-foreground truncate text-[13px] font-semibold leading-tight"
                  >
                    {{ created.label }}
                  </div>
                </div>
              </SidebarMenuButton>
              <!-- Future post-create steps render here, under the port call. -->
            </SidebarMenuItem>
          </TransitionGroup>
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  </SidebarProvider>
</template>

<style scoped>
  /* Creation steps collapse up and fade out in a top-to-bottom cascade (each
     step delayed by its index); the created port call eases in once the last
     step has cleared. Honour reduced-motion by skipping the animation. */
  .step-collapse-leave-active {
    overflow: hidden;
    transition:
      opacity 180ms ease,
      transform 200ms ease,
      max-height 240ms ease,
      margin 240ms ease;
    transition-delay: calc(var(--leave-i, 0) * 70ms);
  }
  .step-collapse-leave-from {
    max-height: 72px;
  }
  .step-collapse-leave-to {
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-4px);
  }
  .step-collapse-enter-active {
    transition:
      opacity 260ms ease,
      transform 260ms ease;
    /* wait for the staggered collapse to finish before appearing */
    transition-delay: calc(var(--leave-count, 4) * 70ms + 120ms);
  }
  .step-collapse-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }
  .step-collapse-move {
    transition: transform 240ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .step-collapse-leave-active,
    .step-collapse-enter-active,
    .step-collapse-move {
      transition: none;
      transition-delay: 0ms;
    }
  }
</style>
